import React, { useState, useMemo } from 'react';
import { Button } from 'components/shadcn/ui/button';
import { Alert, AlertDescription } from 'components/shadcn/ui/alert';
import { useGetIDsFromURL } from 'components/DeviceDetails/helper';
import { ProfileTypes } from 'components/DeviceDetails/constants';
import { useDeviceLastStat } from 'components/DeviceDetails/service';
import { useAvailableFirmware } from './service';
import { LightTooltip } from 'components/HGTooltip';
import UpgradeWarning from './UpgradeWarning';
import { updateFirmware } from './service';
import UpgradeProgress from '../UpgradeProgress';
import { Skeleton } from 'components/shadcn/ui/skeleton';
import { Info } from 'lucide-react';
import { useJwtToken } from 'helper';
import { useToast } from 'components/shadcn/hooks/use-toast';

const getTooltipText = (online, currentTooHigh) => {
    if (!online) {
        return 'Battery is currently offline.';
    } else if (currentTooHigh) {
        return 'Battery current is too high';
    }
    return '';
};

const UpgradeFirmwareButton = ({ online, upgradeState }) => {
    const accessToken = useJwtToken();
    const { Thing_ID } = useGetIDsFromURL();
    const { data: availableFirmware, isLoading: firmwareLoading } =
        useAvailableFirmware(Thing_ID);
    const {
        data: lastStat,
        isLoading: statsLoading,
        SINGLE_BMS_ID,
        deviceType,
    } = useDeviceLastStat(Thing_ID);
    const { toast } = useToast();
    const [openModal, setOpenModal] = useState(false);
    const [initiated, setInitiated] = useState(false);
    const currentTooHigh = lastStat?.E_cur > 10 ? true : false;
    const tooltipText = useMemo(
        () => getTooltipText(online, currentTooHigh),
        [online, currentTooHigh]
    );

    if (
        (!SINGLE_BMS_ID && deviceType !== ProfileTypes.EMS) ||
        (!availableFirmware?.Name && !upgradeState)
    ) {
        return null;
    }

    if (statsLoading || firmwareLoading) {
        return (
            <Alert className="flex w-max items-center gap-3 bg-muted/40">
                <AlertDescription className="flex items-center gap-2">
                    <Skeleton className="h-5 w-5 rounded-full" />
                    <Skeleton className="h-4 w-48" />
                </AlertDescription>
                <div className="ml-auto">
                    <Skeleton className="h-[40px] w-[140px]" />
                </div>
            </Alert>
        );
    }

    const startFirmwareUpgradeFn = async () => {
        try {
            const upgradeFirmwareResponse = await updateFirmware(
                Thing_ID,
                availableFirmware.ID,
                accessToken
            );
            if (
                upgradeFirmwareResponse?.publishResult['$metadata'][
                    'httpStatusCode'
                ] === 200
            ) {
                toast({
                    title:
                        upgradeFirmwareResponse?.message ??
                        'Upgrade has been initiated',
                    description:
                        'Battery will restart multiple times during upgrade process. Please do not interrupt and wait till upgrade is complete.',
                    variant: 'default',
                });
            }
            setInitiated(true);
        } catch (error) {
            console.error('Error starting firmware upgrade:', error.message);
            toast({
                title: 'Firmware Upgrade Failed',
                description:
                    'Please try again later. Contact support if the issue persists.',
                variant: 'destructive',
            });
            setInitiated(false);
        } finally {
            setOpenModal(false);
        }
    };

    const initiatedState = initiated ? 'Initiated' : null;
    if (upgradeState || initiatedState) {
        const status = upgradeState || initiatedState;
        return <UpgradeProgress status={status} />;
    }

    return (
        <Alert className="flex w-max items-center gap-3 bg-muted/40">
            <AlertDescription className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                {`${availableFirmware?.Name} is available`}
            </AlertDescription>
            <div className="ml-auto">
                <LightTooltip title={tooltipText} followCursor arrow={false}>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setOpenModal(true)}
                        disabled={!online || currentTooHigh}
                        className="h-[60px] w-full md:h-[40px]"
                    >
                        Update Firmware
                    </Button>
                </LightTooltip>
                <UpgradeWarning
                    open={openModal}
                    close={() => setOpenModal(false)}
                    startFirmwareUpgrade={startFirmwareUpgradeFn}
                />
            </div>
        </Alert>
    );
};

export default UpgradeFirmwareButton;
